import { useCallback, useEffect, useMemo, useState } from 'react';
import { Add, DeleteOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import uuid from 'react-uuid';
import { FirmDailyNavConfigVersion } from 'api';
import {
  DAILY_NAV_DEFAULT_TRADING_MARKET,
  DAILY_NAV_FIRM_DEFAULTS_COPY,
  DAILY_NAV_THRESHOLD_GROUPS,
  DAILY_NAV_THRESHOLD_KEYS,
  DAILY_NAV_TRADING_MARKET_OPTIONS,
  DailyNavThresholdKey,
  DailyNavTradingMarket,
  fractionToPercent,
  isValidThresholdPercent,
  percentToFraction,
  tradingMarketLabel,
} from 'common/constants/dailyNav';
import { DAILY_NAV_SETTINGS_PAGE_TITLE, FIRM_SETTINGS } from 'common/constants/firms';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { GridSkeleton } from 'components/Grid';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { buildFirmSettingsNavigationItems } from 'pages/Firms/firmSettingsNavigation';
import { useReadUserAccessLevel } from 'services/hooks';
import {
  useGetFirmDailyNavConfig,
  useGetFirmDailyNavConfigHistory,
  useUpdateFirmDailyNavConfig,
} from 'services/hooks/dailyNav';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_THRESHOLDS = Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, ''])) as Record<
  DailyNavThresholdKey,
  string
>;

type RecipientRow = { email: string; key: string };

const formatEffectivePeriod = (version: FirmDailyNavConfigVersion) => {
  const from = format(new Date(version.effective_from), 'MMM d, yyyy h:mm a');
  const to = version.effective_to ? format(new Date(version.effective_to), 'MMM d, yyyy h:mm a') : 'Present';
  return `${from} — ${to}`;
};

const DailyNavSettings = () => {
  const [{ firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const firmId = firmInfo?.id ?? null;
  const firmSlug = firmInfo?.slug ?? '';

  const { isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const { data: config, isLoading } = useGetFirmDailyNavConfig(firmId);
  const { history, loadMoreHistory, hasMoreHistory, isLoadingMoreHistory } = useGetFirmDailyNavConfigHistory(firmId);
  const { updateFirmDailyNavConfig, isPending } = useUpdateFirmDailyNavConfig();

  const [thresholds, setThresholds] = useState<Record<DailyNavThresholdKey, string>>(EMPTY_THRESHOLDS);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [tradingMarket, setTradingMarket] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  // The firm has never saved a template: the API returns unpersisted model
  // defaults (no id) just so the page has something to render. Leave the
  // form blank rather than pre-filling those defaults, so the admin has to
  // deliberately enter every threshold before the first version can be saved.
  const hasSavedConfig = Boolean(config?.id);

  useEffect(() => {
    if (config) {
      setThresholds(
        hasSavedConfig
          ? (Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(config[key])])) as Record<
              DailyNavThresholdKey,
              string
            >)
          : EMPTY_THRESHOLDS
      );
      const configRecipients =
        hasSavedConfig && Array.isArray(config.notification_recipients)
          ? (config.notification_recipients as string[])
          : [];
      setRecipients(configRecipients.map(email => ({ email, key: uuid() })));
      // Unlike the thresholds, the market is seeded when nothing is saved yet.
      setTradingMarket(
        hasSavedConfig ? (config.trading_calendar ?? '') : DAILY_NAV_DEFAULT_TRADING_MARKET
      );
    }
  }, [config, hasSavedConfig]);

  const isFormValid = useMemo(
    () =>
      DAILY_NAV_THRESHOLD_KEYS.every(key => isValidThresholdPercent(thresholds[key])) &&
      recipients.every(recipient => EMAIL_PATTERN.test(recipient.email.trim())) &&
      Boolean(tradingMarket),
    [thresholds, recipients, tradingMarket]
  );

  const isDirty = useMemo(() => {
    const baselineThresholds = hasSavedConfig
      ? (Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(config?.[key])])) as Record<
          DailyNavThresholdKey,
          string
        >)
      : EMPTY_THRESHOLDS;
    const thresholdsChanged = DAILY_NAV_THRESHOLD_KEYS.some(key => thresholds[key] !== baselineThresholds[key]);

    const baselineRecipients =
      hasSavedConfig && Array.isArray(config?.notification_recipients)
        ? (config?.notification_recipients as string[])
        : [];
    const currentRecipients = recipients.map(recipient => recipient.email.trim());
    const recipientsChanged =
      currentRecipients.length !== baselineRecipients.length ||
      currentRecipients.some((email, index) => email !== baselineRecipients[index]);

    const baselineMarket = hasSavedConfig
      ? (config?.trading_calendar ?? '')
      : DAILY_NAV_DEFAULT_TRADING_MARKET;
    const marketChanged = tradingMarket !== baselineMarket;

    return thresholdsChanged || recipientsChanged || marketChanged;
  }, [config, hasSavedConfig, thresholds, recipients, tradingMarket]);

  const canSave = isFormValid && isDirty;

  // A stored value could predate the curated list; append it rather than
  // rendering an empty picker.
  const marketOptions = useMemo(() => {
    if (!tradingMarket || DAILY_NAV_TRADING_MARKET_OPTIONS.some(option => option.value === tradingMarket)) {
      return DAILY_NAV_TRADING_MARKET_OPTIONS;
    }
    return [...DAILY_NAV_TRADING_MARKET_OPTIONS, { value: tradingMarket, label: tradingMarket }];
  }, [tradingMarket]);

  const saveDailyNavSettings = useCallback(async () => {
    if (!firmId || !canSave) {
      return;
    }
    const updatedConfig = await updateFirmDailyNavConfig(firmId, {
      cap_iq_one_day_delta: percentToFraction(thresholds.cap_iq_one_day_delta),
      cap_iq_five_day_delta: percentToFraction(thresholds.cap_iq_five_day_delta),
      cap_iq_since_last_valuation_delta: percentToFraction(thresholds.cap_iq_since_last_valuation_delta),
      secondary_one_day_delta: percentToFraction(thresholds.secondary_one_day_delta),
      secondary_five_day_delta: percentToFraction(thresholds.secondary_five_day_delta),
      secondary_since_last_valuation_delta: percentToFraction(thresholds.secondary_since_last_valuation_delta),
      notification_recipients: recipients.map(recipient => recipient.email.trim()),
      // The generated read and write models declare separate identical enums,
      // and TS string enums are nominal, so the cast is required.
      trading_calendar: tradingMarket as DailyNavTradingMarket,
    });
    if (updatedConfig) {
      enqueueSnackbar('Daily NAV settings successfully saved', { variant: 'success' });
    }
  }, [firmId, canSave, thresholds, recipients, tradingMarket, updateFirmDailyNavConfig, enqueueSnackbar]);

  const secondaryNavigation = useMemo(
    () => ({
      title: FIRM_SETTINGS,
      items: buildFirmSettingsNavigationItems(firmSlug, { dailyNavEnabled: true }),
      actions: isAdminOrStaffOrSuperuser
        ? [
          {
            type: 'button' as const,
            key: 'firm-daily-nav-settings-save',
            label: 'Save',
            variant: 'positive' as const,
            onClick: saveDailyNavSettings,
            disabled: !firmId || !canSave || isPending,
          },
        ]
        : [],
    }),
    [firmSlug, isAdminOrStaffOrSuperuser, saveDailyNavSettings, firmId, canSave, isPending]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  const selectedVersion = useMemo(
    () => history?.find(version => version.id === selectedVersionId) ?? null,
    [history, selectedVersionId]
  );

  const handleAddRecipient = () => {
    setRecipients(prevState => [...prevState, { email: '', key: uuid() }]);
  };

  const handleRemoveRecipient = (index: number) => {
    setRecipients(prevState => prevState.filter((_, i) => i !== index));
  };

  const handleRecipientChange = (index: number, value: string) => {
    setRecipients(prevState =>
      prevState.map((recipient, i) => (i === index ? { ...recipient, email: value } : recipient))
    );
  };

  const handleThresholdChange = (key: DailyNavThresholdKey, value: string) => {
    setThresholds(prevState => ({ ...prevState, [key]: value }));
  };

  if (isLoading) {
    return <GridSkeleton />;
  }

  return (
    <Grid container spacing={4} sx={{ mb: 10, pt: 1, flexDirection: { xs: 'column', lg: 'row' } }}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <Typography variant="h6" gutterBottom>
          {DAILY_NAV_SETTINGS_PAGE_TITLE}
        </Typography>
        <Typography variant="h4" gutterBottom sx={{ mt: 3 }}>
          Default Alert Thresholds
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {DAILY_NAV_FIRM_DEFAULTS_COPY}
        </Typography>
        {!hasSavedConfig && (
          <Alert severity="info" sx={{ mb: 3 }} data-testid="daily-nav-no-config-alert">
            This firm has not configured Daily NAV monitoring yet. Enter a value for every threshold below, then Save to
            activate monitoring.
          </Alert>
        )}
        {hasSavedConfig && !isDirty && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}
            data-testid="daily-nav-no-changes-hint">
            No changes to save.
          </Typography>
        )}
        {DAILY_NAV_THRESHOLD_GROUPS.map(group => (
          <Box key={group.title} sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              {group.title.toUpperCase()}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {group.fields.map(field => (
                <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    id={`daily-nav-threshold-${field.key}`}
                    label={field.label}
                    value={thresholds[field.key]}
                    onChange={e => handleThresholdChange(field.key, e.target.value)}
                    error={!isValidThresholdPercent(thresholds[field.key])}
                    disabled={!isAdminOrStaffOrSuperuser}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h4" gutterBottom>
          Trading Market
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The market whose trading calendar defines the NAV day. Its session close is when market data is read up to,
          and allocations finalized by then are the ones included in that day&apos;s report. Trading holidays are not NAV
          days, and early closes are handled automatically. Each day uses the market that was in effect on that date.
        </Typography>
        {/* The picker reads as choosing where comp prices come from; say it does not. */}
        <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }} color="text.secondary">
          This sets the day&apos;s timing only. It does not affect which public comps are tracked or where their prices
          come from &mdash; those are set per company in Daily Monitoring.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField
              select
              fullWidth
              size="small"
              id="daily-nav-trading-market"
              label="Trading Market"
              value={tradingMarket}
              onChange={e => setTradingMarket(e.target.value)}
              error={!tradingMarket}
              disabled={!isAdminOrStaffOrSuperuser}>
              {marketOptions.map(option => (
                <MenuItem key={option.value} value={option.value} data-testid={`daily-nav-market-${option.value}`}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h4" gutterBottom>
          Daily Alert Notification Recipients
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          These recipients will receive the daily alert notifications.
        </Typography>
        <Stack spacing={2}>
          {recipients.map((recipient, index) => (
            <Grid container spacing={2} key={recipient.key} alignItems="center">
              <Grid size={{ xs: 10, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="e.g. alerts@firm.com"
                  data-testid={`daily-nav-recipient-${index}`}
                  value={recipient.email}
                  onChange={e => handleRecipientChange(index, e.target.value)}
                  error={Boolean(recipient.email) && !EMAIL_PATTERN.test(recipient.email.trim())}
                  disabled={!isAdminOrStaffOrSuperuser}
                />
              </Grid>
              <Grid size={{ xs: 2, sm: 1 }} sx={{ textAlign: 'right' }}>
                <IconButton
                  color="error"
                  data-testid={`daily-nav-recipient-remove-${index}`}
                  onClick={() => handleRemoveRecipient(index)}
                  disabled={!isAdminOrStaffOrSuperuser}>
                  <DeleteOutline />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<Add />}
            onClick={handleAddRecipient}
            variant="text"
            id="daily-nav-add-recipient"
            disabled={!isAdminOrStaffOrSuperuser}>
            Add Recipient
          </Button>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, lg: 5 }} sx={{ mt: { xs: 2, lg: '1.25em' } }}>
        <Paper elevation={4} sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Version History
          </Typography>
          {history?.length ? (
            <List dense>
              {history.map(version => (
                <ListItemButton
                  key={version.id}
                  selected={version.id === selectedVersionId}
                  data-testid={`daily-nav-firm-version-${version.id}`}
                  onClick={() => setSelectedVersionId(version.id === selectedVersionId ? null : (version.id ?? null))}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{formatEffectivePeriod(version)}</span>
                        {version.is_active && (
                          <Chip
                            size="small"
                            color="success"
                            label="Current"
                            data-testid={`daily-nav-firm-version-current-${version.id}`}
                          />
                        )}
                      </Stack>
                    }
                    secondary={version.created_by_name ? `Changed by ${version.created_by_name}` : undefined}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No configuration changes have been saved yet.</Typography>
          )}
          {hasMoreHistory && (
            <Button
              fullWidth
              size="small"
              onClick={() => loadMoreHistory()}
              disabled={isLoadingMoreHistory}
              data-testid="daily-nav-firm-history-load-more">
              {isLoadingMoreHistory ? 'Loading...' : 'Load More'}
            </Button>
          )}
          {selectedVersion && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Values in effect {formatEffectivePeriod(selectedVersion)}
              </Typography>
              {DAILY_NAV_THRESHOLD_GROUPS.map(group => (
                <Box key={group.title} sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {group.title.toUpperCase()}
                  </Typography>
                  {group.fields.map(field => (
                    <Typography key={field.key} variant="body2">
                      {field.label}: {fractionToPercent(selectedVersion[field.key]) || '—'}%
                    </Typography>
                  ))}
                </Box>
              ))}
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                TRADING MARKET
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {tradingMarketLabel(selectedVersion.trading_calendar)}
              </Typography>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                NOTIFICATION RECIPIENTS
              </Typography>
              <Typography variant="body2">
                {Array.isArray(selectedVersion.notification_recipients) &&
                selectedVersion.notification_recipients.length
                  ? (selectedVersion.notification_recipients as string[]).join(', ')
                  : 'None'}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DailyNavSettings;
